export type CustomerChatSession = {
  contactId: number;
  name: string | null;
  avatarUrl: string | null;
  lastFocusedAt: number;
};

export type CustomerChatMode = "closed" | "fullscreen" | "minimized";

export type CustomerChatRightTab = "profile" | "hermes";

export type CustomerChatState = {
  sessions: CustomerChatSession[];
  activeContactId: number | null;
  mode: CustomerChatMode;
  rightTab: CustomerChatRightTab;
};

export function initialCustomerChatState(): CustomerChatState {
  return {
    sessions: [],
    activeContactId: null,
    mode: "closed",
    rightTab: "profile",
  };
}

let lastFocusTimestamp = 0;

function nextLastFocusedAt(explicit?: number): number {
  if (explicit !== undefined) {
    lastFocusTimestamp = Math.max(lastFocusTimestamp, explicit);
    return explicit;
  }
  const now = Date.now();
  lastFocusTimestamp = Math.max(now, lastFocusTimestamp + 1);
  return lastFocusTimestamp;
}

/** 在当前聚焦时间基础上单调递增，避免同毫秒批量测试/操作时 Dock 排序不稳定 */
function bumpLastFocusedAt(current: number): number {
  lastFocusTimestamp = Math.max(lastFocusTimestamp, current) + 1;
  return lastFocusTimestamp;
}

export function openSession(
  state: CustomerChatState,
  session: Omit<CustomerChatSession, "lastFocusedAt"> & { lastFocusedAt?: number },
): CustomerChatState {
  const lastFocusedAt = nextLastFocusedAt(session.lastFocusedAt);
  const existingIndex = state.sessions.findIndex((s) => s.contactId === session.contactId);

  let sessions: CustomerChatSession[];
  if (existingIndex >= 0) {
    const updated: CustomerChatSession = {
      contactId: session.contactId,
      name: session.name,
      avatarUrl: session.avatarUrl,
      lastFocusedAt,
    };
    sessions = [
      ...state.sessions.slice(0, existingIndex),
      ...state.sessions.slice(existingIndex + 1),
      updated,
    ];
  } else {
    sessions = [
      ...state.sessions,
      {
        contactId: session.contactId,
        name: session.name,
        avatarUrl: session.avatarUrl,
        lastFocusedAt,
      },
    ];
  }

  return {
    sessions,
    activeContactId: session.contactId,
    mode: "fullscreen",
    rightTab: "profile",
  };
}

export function minimizeChat(state: CustomerChatState): CustomerChatState {
  if (state.mode !== "fullscreen") {
    return state;
  }

  const sessions = state.sessions.map((s) =>
    s.contactId === state.activeContactId
      ? { ...s, lastFocusedAt: bumpLastFocusedAt(s.lastFocusedAt) }
      : s,
  );

  return {
    ...state,
    sessions,
    mode: "minimized",
  };
}

export function closeActiveSession(state: CustomerChatState): CustomerChatState {
  if (state.activeContactId === null) {
    return state;
  }

  const sessions = state.sessions.filter((s) => s.contactId !== state.activeContactId);

  if (sessions.length === 0) {
    return {
      sessions: [],
      activeContactId: null,
      mode: "closed",
      rightTab: state.rightTab,
    };
  }

  const mostRecent = sessions.reduce((latest, s) =>
    s.lastFocusedAt > latest.lastFocusedAt ? s : latest,
  );

  return {
    sessions,
    activeContactId: mostRecent.contactId,
    mode: "minimized",
    rightTab: state.rightTab,
  };
}

export function focusSession(state: CustomerChatState, contactId: number): CustomerChatState {
  const session = state.sessions.find((s) => s.contactId === contactId);
  if (!session) {
    return state;
  }

  const sessions = state.sessions.map((s) =>
    s.contactId === contactId ? { ...s, lastFocusedAt: nextLastFocusedAt() } : s,
  );

  return {
    ...state,
    sessions,
    activeContactId: contactId,
    mode: "fullscreen",
    rightTab: "profile",
  };
}

export function setRightTab(
  state: CustomerChatState,
  tab: CustomerChatRightTab,
): CustomerChatState {
  return {
    ...state,
    rightTab: tab,
  };
}

export function dockSessions(state: CustomerChatState): CustomerChatSession[] {
  if (state.mode !== "minimized") {
    return [];
  }

  return [...state.sessions]
    .sort((a, b) => b.lastFocusedAt - a.lastFocusedAt)
    .slice(0, 3);
}
